// Slot identity for pedigree data overlays (TGCLOUD-1670).
//
// Colour belongs to the SLOT, never to the datum. Two overlays can be on at
// once and the reader has to tell them apart at a glance without learning a
// colour code per criterion, so slot A is always the solid ring in one colour
// and slot B always the dashed ring in the other, whatever they are showing.
//
// ── Why these two hexes, and why the previous pair was unsafe ────────────────
//
// The first cut used #ad1457 and #2e7d32 and justified them with a comment
// claiming both sat "outside every register already spoken for on this canvas".
// That claim was wrong three times over, and the comment is reproduced here only
// so the same reasoning is not repeated:
//
//   1. #2e7d32 is BYTE-IDENTICAL to CONFIRMED_TICK_COLOUR (Canvas.jsx), the
//      green of the clinically-confirmed-diagnosis tick AND the
//      classification-reviewed tick. Its own declaration carries a comment
//      saying it must never drift, because it is "the same signal to a reader".
//      A dashed green ring and a green tick on one node asserted
//      "administratively flagged" and "clinically verified" in one hue.
//   2. #ad1457 measures 15.4 from disease-palette Red and sits inside a
//      red/crimson band already carrying four meanings: pathogenic variant
//      #D93025, another-user lock #d32f2f, disease Red #e6194B and Maroon
//      #800000.
//   3. It said "the disease palette owns the rest" as though the palette were
//      closed. It is not: the disease ColorPicker takes an arbitrary typed hex,
//      and past twenty diseases the colour is generated on a golden-angle sweep.
//      THE DISEASE REGISTER IS OPEN, so no hue can be reserved by argument.
//
// The measured consequence of (3) is the fact that decides this design: over the
// whole sRGB gamut the maximum achievable CIEDE2000 distance to every clinical
// colour on this canvas is about 24.8, at #3c3c00, whose nearest neighbour is
// the confirmed green. Twelve taught hue codes plus an open tenant-typed disease
// palette plus ink and paper tile the space. At a 45-degree hue veto, zero of
// 360 degrees remain free. HUE CANNOT CARRY THE SAFETY HERE.
//
// So the safety comes from three things that are not hue:
//
//   - CHANNEL. The mark is a ring OUTSIDE the symbol and a pill ABOVE it. Every
//     clinical hue code on this canvas is a fill inside the symbol, a perimeter
//     on its edge, a small dot or tick beside a label, or a corner badge. A
//     disease colour and an overlay colour can never be the same mark.
//   - DISTANCE FROM EVERY CLAIM. Measured below: nothing whose hue asserts a
//     clinical fact about the patient is within 27.8 of either slot.
//   - REDUNDANT CODING. Solid versus dashed, the pill text, the legend row and
//     the results row all carry the meaning. This is load-bearing rather than
//     decorative: both slots are dark (L* 26.1 and 24.1, a greyscale contrast
//     of about 1.07 : 1), so in a monochrome rendering the two rings are the
//     same tone and ONLY solid-versus-dashed separates them.
//
// Note what is NOT claimed. Slot A is genuinely low chroma (C* 21.8), in the
// desaturated register neutral ink occupies; slot B is not (C* 33.1), which is
// right at the boundary where the clinical hue codes begin. Slot B's protection
// is its measured distance, not its saturation.
//
// Measured for the shipped pair (CIEDE2000, verified against the Sharma 2005
// reference vectors, and re-derived independently by a second pass):
//
//   nearest clinical colour   slot A 19.7 (#000075 disease Navy), slot B 22.1
//                             (#9A6324 disease Brown) — both tenant-mutable
//                             disease fills, i.e. a different channel
//   confirmed-diagnosis green >= 29.3   (was 0.0)
//   whole red / pink register >= 28.9   (was 15.4)
//   cascade amber             >= 27.8
//   slot A versus slot B      36.2
//   contrast on white         10.7 : 1 and 11.5 : 1
//   contrast on the dark
//   theme ground (#DDDDDD)    7.9 : 1
//
// Everything above is reproduced by `slotColours.test.js`, which validates its
// CIEDE2000 against the Sharma 2005 reference vectors first so a broken metric
// cannot pass the distance assertions for free.
//
// One further finding, from the colour study rather than from that test: under
// the Vienot 1999 dichromacy projection the OLD pair collapsed to about 11.7,
// so its two rings were near-indistinguishable to a red-green colour-blind
// reader — a defect nobody had reported. This pair measured 40.8 (deuteranopia)
// and 37.3 (protanopia). Those figures are NOT pinned by a test: a hand-rolled
// simulation produced nonsense and there are no reference vectors here to
// validate one against, so the number is attributed rather than asserted.
//
// Known residual, accepted and recorded rather than hidden: slot A measures 10.1
// from #354051, which is NOTE_TYPE_COLOURS.Test and 'Risk Score' — record-type
// chrome on the sidebar cards. That is a confusion between an annotation and a
// record-type chip, not between an annotation and a clinical assertion, and no
// candidate at this lightness improves it without breaking something worse.
//
// `slotColours.test.js` pins all of this against the real constants imported
// from the code, so neither side can drift into the other again.

export const MAX_DATA_OVERLAY_SLOTS = 2;

export const DATA_OVERLAY_SLOT_COLOURS = Object.freeze([
  '#00435C', // slot A, petrol blue, solid ring
  '#463800' // slot B, dark bronze, dashed ring
]);

/** Both are dark enough that the pill's bold text is white on either. */
export const DATA_OVERLAY_PILL_TEXT = '#ffffff';

export const slotColour = (index) =>
  DATA_OVERLAY_SLOT_COLOURS[index] || DATA_OVERLAY_SLOT_COLOURS[0];

/** A slot the clinician has added but whose facts have not arrived yet. */
export const emptySlot = (presetId, params = {}) => ({
  presetId,
  params,
  enabled: true
});

export const TREATMENTS = Object.freeze([
  { value: 'ring', label: 'Ring' },
  { value: 'dim', label: 'Dim others' }
]);
